import DOMPurify from 'isomorphic-dompurify'

import { LOGROCKET_SENSITIVE_FIELDS } from '@/utils/constants'

export function maskSensitiveFields(
  body: string | null | undefined,
): string | undefined {
  if (!body) return undefined
  try {
    const parsed = JSON.parse(body)
    for (const field of LOGROCKET_SENSITIVE_FIELDS) {
      if (field in parsed) parsed[field] = '[REDACTED]'
    }
    return JSON.stringify(parsed)
  } catch {
    return body
  }
}

type SanitizeConfig = {
  ALLOWED_TAGS?: string[]
  ALLOWED_ATTR?: string[]
  ALLOW_DATA_ATTR?: boolean
  KEEP_CONTENT?: boolean
}

export const sanitizeHTML = (html: string, config?: SanitizeConfig): string => {
  if (!html) return ''

  const defaultConfig: SanitizeConfig = {
    ALLOWED_TAGS: [
      'p',
      'br',
      'strong',
      'em',
      'u',
      's',
      'a',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'ul',
      'ol',
      'li',
      'blockquote',
      'code',
      'pre',
      'span',
      'div',
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style'],
    ALLOW_DATA_ATTR: false,
    KEEP_CONTENT: true,
  }

  return DOMPurify.sanitize(html, { ...defaultConfig, ...config }) as string
}

export const sanitizeComment = (html: string): string => {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'a'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
  }) as string
}

export const stripHTML = (html: string): string => {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [],
    KEEP_CONTENT: true,
  }) as string
}

export const sanitizeEmail = (html: string): string => {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      // Document structure
      'html',
      'head',
      'body',
      'title',
      'meta',
      'style',
      // Layout elements
      'div',
      'span',
      'p',
      'br',
      'hr',
      // Headers
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      // Text formatting
      'strong',
      'b',
      'em',
      'i',
      'u',
      's',
      'strike',
      'small',
      // Links and media
      'a',
      'img',
      // Tables (essential for email layouts)
      'table',
      'thead',
      'tbody',
      'tfoot',
      'tr',
      'th',
      'td',
      'caption',
      'colgroup',
      'col',
      // Lists
      'ul',
      'ol',
      'li',
      // Other semantic elements
      'blockquote',
      'code',
      'pre',
      'center',
      'font',
    ],
    ALLOWED_ATTR: [
      'href',
      'target',
      'rel',
      'class',
      'style',
      'src',
      'alt',
      'width',
      'height',
      'align',
      'valign',
      'border',
      'cellpadding',
      'cellspacing',
      'bgcolor',
      'color',
      'size',
      'face',
      'name',
      'content',
      'charset',
    ],
    ALLOW_DATA_ATTR: false,
    KEEP_CONTENT: true,
  }) as string
}
